import { useEffect, useState } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Pause, Play, ArrowRightLeft, Copy, Check } from 'lucide-react';
import { useSoftphone } from '@/store/softphone';
import { SignalBars } from './SignalBars';
import clsx from 'clsx';

/**
 * Full-workspace active call display. Two layouts:
 *  - normal: big avatar + name + timer + 4 chips + big hangup
 *  - keypad open: compact header banner + DTMF grid + bottom action row
 */
export function ActiveCallCard() {
  const { state, call, muted, onHold, toggleMute, toggleHold, hangup, openTransferSheet } = useSoftphone();
  const [seconds, setSeconds] = useState(0);
  const [copied, setCopied] = useState(false);

  // NOTE: audio sink lives in <CallAudio> at AppShell level (route-agnostic).
  // Transfer sheet aynı şekilde global — openTransferSheet() ile açılır,
  // <TransferSheet /> AppShell'de render edilir.

  useEffect(() => {
    if (state !== 'in_call' || !call?.answeredAt) { setSeconds(0); return; }
    const tick = () => setSeconds(Math.floor((Date.now() - call.answeredAt!) / 1000));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [state, call?.answeredAt]);

  if (!call) return null;

  // Compute initials only from displayName letters. Phone numbers → null → fallback to Phone icon.
  const letterSource = (call.displayName || '').replace(/[^a-zA-ZçğıöşüÇĞİÖŞÜ ]/g, ' ').trim();
  const initials = letterSource
    ? letterSource.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('')
    : null;

  const ringColor = state === 'in_call' ? 'from-emerald-500 to-emerald-600'
    : state === 'ringing' ? 'from-amber-400 to-amber-500'
    : 'from-blue-500 to-blue-600';

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(call.peer);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* */ }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center p-6 gap-4">
        <div className={clsx(
          'w-28 h-28 rounded-full bg-gradient-to-br text-white flex items-center justify-center font-bold shadow-[0_8px_24px_-4px_rgba(0,0,0,0.2)]',
          ringColor,
          state === 'ringing' && 'animate-pulse',
        )}>
          {initials
            ? <span className="text-4xl">{initials}</span>
            : <Phone size={44} strokeWidth={2.2} />}
        </div>

        <div className="text-center">
          {call.displayName && (
            <div className="text-xl font-semibold text-slate-900 tracking-tight mb-0.5">{call.displayName}</div>
          )}
          <div className="flex items-center justify-center gap-1.5 text-slate-600 font-mono text-base">
            {call.peer}
            <button
              onClick={copyNumber}
              className="text-slate-400 hover:text-blue-600 p-1 transition"
              title="Numarayı kopyala"
            >
              {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
            </button>
          </div>
        </div>

        {state === 'in_call' ? (
          <div className="text-3xl font-mono font-semibold text-slate-700 tabular-nums tracking-tight text-center">
            {fmtTime(seconds)}
            <div className="text-[11px] font-sans text-slate-400 mt-0.5 font-normal">
              görüşme süresi
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2.5">
              <span className="w-3.5 h-3.5 rounded-full bg-blue-500 animate-[pulse_1.4s_ease-in-out_infinite] [animation-delay:0ms]" />
              <span className="w-3.5 h-3.5 rounded-full bg-blue-500 animate-[pulse_1.4s_ease-in-out_infinite] [animation-delay:200ms]" />
              <span className="w-3.5 h-3.5 rounded-full bg-blue-500 animate-[pulse_1.4s_ease-in-out_infinite] [animation-delay:400ms]" />
            </div>
            <div className="text-base text-slate-600 font-medium tracking-tight">
              {state === 'calling' && 'Aranıyor…'}
              {state === 'ringing' && (call.direction === 'inbound' ? 'gelen çağrı' : 'Çalıyor…')}
            </div>
          </div>
        )}

        {state === 'in_call' && (
          <div className="mt-1">
            <SignalBars />
          </div>
        )}
      </div>

      {state === 'in_call' && (
        <div className="border-t border-slate-100 px-3 pt-3 pb-4 flex flex-col items-center gap-3">
          <div className="flex items-center gap-2 w-full justify-center">
            <ActionChip
              icon={muted ? MicOff : Mic}
              label={muted ? 'Mic Açık' : 'Sustur'}
              active={muted}
              onClick={toggleMute}
            />
            <ActionChip
              icon={onHold ? Play : Pause}
              label={onHold ? 'Devam' : 'Beklet'}
              active={onHold}
              onClick={toggleHold}
            />
            <ActionChip
              icon={ArrowRightLeft}
              label="Aktar"
              onClick={openTransferSheet}
            />
          </div>

          <button
            onClick={hangup}
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 active:bg-red-700 text-white flex items-center justify-center shadow-[0_8px_20px_-4px_rgba(239,68,68,0.55)] transition active:scale-95"
          >
            <PhoneOff size={24} />
          </button>
        </div>
      )}

      {(state === 'calling' || state === 'ringing') && call.direction === 'outbound' && (
        <div className="border-t border-slate-100 p-4 flex justify-center">
          <button
            onClick={hangup}
            className="px-5 py-2.5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center gap-2 shadow-[0_4px_12px_-3px_rgba(239,68,68,0.45)] text-sm font-semibold"
          >
            <PhoneOff size={16} /> İptal
          </button>
        </div>
      )}

      {state === 'ringing' && call.direction === 'inbound' && (
        <div className="border-t border-slate-100 p-5 flex justify-center">
          <button
            onClick={() => useSoftphone.getState().accept()}
            className="flex items-center gap-2 px-7 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-[#08140d] text-sm font-semibold shadow-[0_4px_14px_-3px_rgba(16,185,129,0.5)] transition active:scale-95"
          >
            <Phone size={16} /> Cevapla
          </button>
        </div>
      )}

    </div>
  );
}

// TransferSheet kaldırıldı — global <TransferSheet /> AppShell'de render edilir,
// openTransferSheet() store action ile açılır. SpeedDials query da orada.

function ActionChip({ icon: Icon, label, active, onClick }: {
  icon: any; label: string; active?: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex flex-col items-center justify-center gap-0.5 flex-1 py-2 rounded-xl transition-all active:scale-95',
        active
          ? 'bg-amber-500 text-[#08140d] shadow-[0_3px_8px_-2px_rgba(245,158,11,0.5)]'
          : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
      )}
    >
      <Icon size={18} />
      <span className="text-[10px] font-medium">{label}</span>
    </button>
  );
}

function fmtTime(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const mm = String(m).padStart(2, '0');
  const rr = String(r).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${rr}` : `${mm}:${rr}`;
}
